import { Controller } from "@hotwired/stimulus"
import { similiar } from "../utils"
import { get } from "@rails/request.js"

// Connects to data-controller="search"
export default class extends Controller {
    static targets = ["combobox", "listbox", "option", "clear"]

    listboxTargetConnected(target) {
        get("/suggestions?frame=options")
            .then(response => response.html)
            .then(html => target.innerHTML = html)
    }

    onDocumentBodyPointerup = (event) => {
        if (!this.element.contains(event.target)) {
            this.closeListbox()
        }
    }

    onComboboxKeydown = (event) => {
        switch (event.key) {
            case "ArrowDown": {
                event.preventDefault()

                if (event.altKey) {
                    this.openListbox()
                } else {
                    this.openListbox()
                    this.selectNextOption()
                }
                break
            }
            case "ArrowUp": {
                event.preventDefault()

                this.openListbox()
                this.selectPrevOption()
                break
            }
            case "Enter": {
                this.closeListbox()
                break
            }
            case "Escape": {
                if (this.isListboxOpen) {
                    this.closeListbox()

                    !this.hasOptions && this.clearCombobox()
                } else {
                    this.clearCombobox()
                }
                break
            }
        }
    }

    clearCombobox() {
        this.comboboxTarget.value = ""
    }

    focusCombobox() {
        this.comboboxTarget.focus()
    }

    openListbox() {
        this.comboboxTarget.setAttribute("aria-expanded", true)
    }

    closeListbox() {
        const combobox = this.comboboxTarget
        const options = this.optionTargets

        options.forEach(option => option.setAttribute("aria-selected", false))
        combobox.setAttribute("aria-expanded", false)
        combobox.setAttribute("aria-activedescendant", "")
    }

    get isListboxOpen() {
        return this.comboboxTarget.getAttribute("aria-expanded") == "true"
    }

    filterOptions() {
        const combobox = this.comboboxTarget
        const term = combobox.value.trim()

        let count = 0
        this.optionTargets.forEach(option => {
            const hasLocale = !!option.dataset.locale

            option.hidden = count >= 6 ||
                (term == "" ? hasLocale : !similiar(term, option.textContent.trim()))
            !option.hidden && count++
            option.setAttribute("aria-selected", false)
        })
        combobox.setAttribute("aria-activedescendant", "")
    }

    selectOption(event) {
        this.#selectOption(event.target)
        this.closeListbox()
        this.element.requestSubmit()
    }

    selectNextOption() {
        const option = this.nextOption
        option && this.#selectOption(option)
    }

    selectPrevOption() {
        const option = this.prevOption
        option && this.#selectOption(option)
    }

    #selectOption(option) {
        const combobox = this.comboboxTarget

        option.scrollIntoView({ block: "nearest" })
        combobox.value = option.textContent.trim()

        combobox.setAttribute("aria-activedescendant", option.id)
        this.optionTargets.forEach((opt) => {
            opt.setAttribute("aria-selected", opt == option)
        })
    }

    get nextOption() {
        const options = this.optionTargets.filter(option => !option.hidden)
        const index = options.findIndex(
            option => option.getAttribute("aria-selected") == "true"
        )
        const nextIndex = (index + 1) % options.length

        return options[nextIndex]
    }

    get prevOption() {
        const options = this.optionTargets.filter(option => !option.hidden)
        const index = options.findIndex(
            option => option.getAttribute("aria-selected") == "true"
        )
        const prevIndex = (index == -1 ? -1 : index - 1) % options.length

        return options.at(prevIndex)
    }

    get hasOptions() {
        return this.optionTargets.some(option => !option.hidden)
    }

    noop() { }

    connect() {
        document.body.addEventListener("pointerup", this.onDocumentBodyPointerup)
    }

    disconnect() {
        document.body.removeEventListener("pointerup", this.onDocumentBodyPointerup)
    }

    comboboxTargetConnected(target) {
        target.addEventListener("keydown", this.onComboboxKeydown)
    }

    comboboxTargetDisconnected(target) {
        target.removeEventListener("keydown", this.onComboboxKeydown)
    }
}
